import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { AppleLogo, GooglePlayLogo, EnvelopeSimple, QrCode } from "@phosphor-icons/react";
import { newsletterApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function DownloadApp() {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [joined, setJoined] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await newsletterApi.join(email);
            setJoined(true);
            toast.success(t("downloadApp.toasts.success"));
        } catch (err) {
            toast.error(t("downloadApp.toasts.error"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="download" className="relative py-32 md:py-40" data-testid="download-app">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <div className="rounded-[32px] overflow-hidden relative" style={{ background: "linear-gradient(140deg, #0B1320 0%, #050A11 100%)", border: "1px solid rgba(212,175,55,0.25)" }}>
                    <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)", filter: "blur(40px)" }} />
                    <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center p-8 md:p-16 relative">
                        <div>
                            <Reveal>
                                <div className="overline mb-4">{t("downloadApp.overline")}</div>
                                <h2 className="font-serif text-5xl md:text-7xl text-white leading-[0.95] tracking-tighter">
                                    {t("downloadApp.title")}
                                </h2>
                                <p className="text-white/60 text-lg leading-relaxed mt-6 max-w-md">
                                    {t("downloadApp.subtitle")}
                                </p>

                                <div className="mt-10 flex flex-wrap gap-4">
                                    <a href="#" className="btn-ghost !bg-black/40" data-testid="app-store">
                                        <AppleLogo size={22} weight="fill" />
                                        <div className="text-left leading-tight">
                                            <div className="text-[10px] uppercase tracking-[0.15em] text-white/60">{t("downloadApp.stores.appStoreSub")}</div>
                                            <div className="text-white text-sm">{t("downloadApp.stores.appStoreTitle")}</div>
                                        </div>
                                    </a>
                                    <a href="#" className="btn-ghost !bg-black/40" data-testid="google-play">
                                        <GooglePlayLogo size={22} weight="fill" />
                                        <div className="text-left leading-tight">
                                            <div className="text-[10px] uppercase tracking-[0.15em] text-white/60">{t("downloadApp.stores.googlePlaySub")}</div>
                                            <div className="text-white text-sm">{t("downloadApp.stores.googlePlayTitle")}</div>
                                        </div>
                                    </a>
                                </div>

                                <form onSubmit={submit} className="mt-10 flex gap-2 max-w-md">
                                    <div className="relative flex-1">
                                        <EnvelopeSimple size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                                        <input
                                            type="email"
                                            required
                                            placeholder={t("downloadApp.placeholder")}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full bg-black/30 border rounded-full pl-11 pr-4 py-3.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[color:var(--gold)] transition-colors"
                                            style={{ borderColor: "rgba(255,255,255,0.1)" }}
                                            data-testid="newsletter-email"
                                        />
                                    </div>
                                    <button type="submit" disabled={loading || joined} className="btn-gold shrink-0" data-testid="newsletter-submit">
                                        {joined ? t("downloadApp.button.joined") : loading ? t("downloadApp.button.sending") : t("downloadApp.button.join")}
                                    </button>
                                </form>
                            </Reveal>
                        </div>

                        <Reveal delay={0.15}>
                            <div className="flex items-center justify-center">
                                <div className="rounded-3xl p-8 grid place-items-center gap-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(212,175,55,0.25)" }}>
                                    <div className="w-52 h-52 rounded-2xl bg-white grid place-items-center">
                                        <QrCode size={180} weight="thin" color="#0B1320" />
                                    </div>
                                    <div className="text-center">
                                        <div className="overline mb-1">{t("downloadApp.qr.overline")}</div>
                                        <div className="font-serif text-lg text-white">{t("downloadApp.qr.platforms")}</div>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
